import { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Input, Modal, Upload, UploadFile, UploadProps, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { nanoid } from '@ant-design/pro-components';
import { FaCamera } from 'react-icons/fa';
import { MdCloudUpload } from 'react-icons/md';
import CustomButton from 'base/ui/components/Button';
import { SearchForm, SearchItem } from 'pages/Components/SearchBox';
import { TransitionButtonModel } from 'models/PurchaseOrder';
import Webcam from 'react-webcam';
import { RcFile } from 'antd/es/upload';
import moment from 'moment';

interface TransitionButtonModalProps {
  open: boolean;
  onClose: () => void;
  buttonInfo: TransitionButtonModel;
  taskId: string;
  wasteHandoverRegistrationCode?: string;
  onUpdate: () => Promise<boolean>;
  onExecute: (data: {
    taskId: string;
    statusTransitionId: string;
    comment?: string;
    files?: string[];
    documentNumber?: string;
  }) => Promise<boolean>;
  onReload?: () => Promise<void>;
}

export const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const TransitionButtonModal: React.FC<TransitionButtonModalProps> = ({
  open,
  onClose,
  buttonInfo,
  taskId,
  wasteHandoverRegistrationCode,
  onUpdate,
  onExecute,
  onReload,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [cameraOpen, setCameraOpen] = useState(false);
  const webcamRef = useRef<any>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [showWarningUpload, setShowWarningUpload] = useState<boolean>(false);
  const [messageUpload, setMessageUpload] = useState<string | undefined>();

  // Hàm chụp ảnh
  const capture = useCallback(() => {
    const today = new Date();
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      const tempt = fileList ? [...fileList] : [];
      if (tempt.length >= 2) {
        setShowWarningUpload(true);
        setMessageUpload('Chỉ được thêm tối đa 2 hình ảnh.');
      } else {
        tempt.push({
          uid: nanoid(),
          thumbUrl: imageSrc,
          preview: imageSrc,
          name: `image_${moment(today).format('YYYY-MM-DD_HH:mm:ss')}`,
        });
        setShowWarningUpload(false);
        setMessageUpload(undefined);
      }
      setFileList(tempt);
      setCameraOpen(false);
    } else {
      message.error('Chụp ảnh thất bại!');
    }
  }, [fileList]);

  // Hàm thay đổi của upload
  const handleChangeUploadFile: UploadProps['onChange'] = (info) => {
    let newFileList = [...info.fileList];
    // Limit the number of uploaded files
    if (newFileList.length > 2) {
      setShowWarningUpload(true);
      setMessageUpload('Chỉ được thêm tối đa 2 hình ảnh.');
      newFileList = newFileList.slice(0, 2);
    } else {
      setShowWarningUpload(false);
      setMessageUpload(undefined);
    }

    // Read from response and show file link
    newFileList = newFileList.map((file) => {
      if (file.response) {
        file.url = file.response.url;
      }
      return file;
    });

    setFileList(newFileList);
  };

  // Xử lý submit
  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // Bước 1: Update form trước
      const updateSuccess = await onUpdate();
      if (!updateSuccess) {
        setLoading(false);
        return;
      }

      // Bước 2: Convert files sang base64
      const files = await Promise.all(
        fileList.map(async (file) => {
          if (file.originFileObj) {
            return await getBase64(file.originFileObj as RcFile);
          } else {
            return file.preview || '';
          }
        })
      );

      // Bước 3: Execute transition button
      const executeData: any = {
        taskId,
        statusTransitionId: buttonInfo.statusTransitionId,
      };

      // Thêm comment nếu có
      if (values.comment) {
        executeData.comment = values.comment;
      }

      // Thêm files nếu có
      const validFiles = files.filter(f => f);
      if (validFiles.length > 0) {
        executeData.files = validFiles;
      }

      // Thêm documentNumber nếu là "Ra cổng"
      if (isExitGate && values.documentNumber) {
        executeData.documentNumber = values.documentNumber;
      }

      const executeSuccess = await onExecute(executeData);

      if (executeSuccess) {
        // Reload data
        if (onReload) {
          await onReload();
        }
        handleClose();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error executing transition button:', error);
      message.error(t('message.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setFileList([]);
    setShowWarningUpload(false);
    setMessageUpload(undefined);
    onClose();
  };

  // Reset form khi modal mở/đóng
  useEffect(() => {
    if (!open) {
      form.resetFields();
      setFileList([]);
      setShowWarningUpload(false);
      setMessageUpload(undefined);
    }
  }, [open, form]);

  // Xác định có phải là "Từ chối vào cổng" không
  const isRejectEntry = buttonInfo.toStatusCode === 'TCVC';
  // Xác định có phải là "Vào cổng" không
  const isCheckIn = buttonInfo.toStatusCode === 'VC';
  // Xác định có phải là "Xác nhận kế hoạch bàn giao" không
  const isConfirmPlan = buttonInfo.toStatusCode === 'XACNHAN';
  // Xác định có phải là "Xác nhận đã cân" không
  const isConfirmWeighed = buttonInfo.toStatusCode === 'CANVAO';
  // Xác định có phải là "Ra cổng" không
  const isExitGate = buttonInfo.toStatusCode === 'RC' || buttonInfo.statusTransitionName === 'Ra cổng';

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        footer={null}
        title={<h2 className="uppercase text-center">{buttonInfo.statusTransitionName}</h2>}
        width={isRejectEntry || isCheckIn || isConfirmPlan || isConfirmWeighed || isExitGate ? 600 : undefined}
      >
        <SearchForm
          form={form}
          onFinish={handleSubmit}
          column="col-1"
        >
          {/* Modal "Xác nhận đã cân" - chỉ hiển thị text và nút xác nhận */}
          {isConfirmWeighed ? (
            <>
              <div className='my-2'>
                <h2 className='text-center text-sm font-medium'>
                  Xác nhận đã cân đầu vào xe giao hàng chất thải/ phế liệu | {wasteHandoverRegistrationCode || ''}
                </h2>
              </div>
              <div className='mt-2 flex justify-end gap-2'>
                <CustomButton
                  color='#52C41A'
                  className='w-full'
                  type='primary'
                  htmlType='submit'
                  loading={loading}
                >
                  Xác nhận
                </CustomButton>
              </div>
            </>
          ) : (
            <>
              {/* Comment/Reason */}
              <SearchItem
                name="comment"
                label={isRejectEntry ? 'Lý do' : 'Ghi chú'}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={
                  isRejectEntry
                    ? [{ required: true, message: 'Vui lòng nhập lý do từ chối.' }]
                    : []
                }
              >
                <Input.TextArea
                  placeholder={isRejectEntry ? 'Nhập lý do...' : 'Nhập ghi chú...'}
                  rows={3}
                />
              </SearchItem>

              {/* Upload file */}
              <SearchItem
                name="checkUpload"
                label={
                  <span>
                    Đính kèm file
                    <span className="font-light text-gray-500"> (Tối đa 2 ảnh)</span>
                  </span>
                }
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                validateStatus={showWarningUpload ? 'warning' : undefined}
                help={messageUpload}
              >
                <Upload
                  accept="image/jpeg,image/png"
                  onChange={handleChangeUploadFile}
                  fileList={fileList}
                  listType="picture"
                  beforeUpload={(file, list) => {
                    if (list.length > 2 || fileList.length >= 2) {
                      setShowWarningUpload(true);
                      setMessageUpload('Chỉ được thêm tối đa 2 hình ảnh.');
                    }
                    return false;
                  }}
                  customRequest={() => { }}
                  onPreview={() => false}
                  multiple
                  maxCount={2}
                  showUploadList={{
                    showDownloadIcon: true,
                    showPreviewIcon: false,
                    showRemoveIcon: true,
                  }}
                  className="[&_.ant-upload-list-item]:w-[220px] [&_.ant-upload-list-item]:float-left [&_.ant-upload-list-item]:me-3"
                >
                  <div className="flex gap-2">
                    <CustomButton
                      icon={<MdCloudUpload />}
                      type="default"
                      htmlType="button"
                    >
                      Tải ảnh lên
                    </CustomButton>
                    <CustomButton
                      icon={<FaCamera />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCameraOpen(true);
                      }}
                      type="default"
                      htmlType="button"
                    >
                      Chụp ảnh
                    </CustomButton>
                  </div>
                </Upload>
              </SearchItem>

              {/* E-goods out (Số chứng từ ra cổng) - chỉ hiển thị khi là "Ra cổng" */}
              {isExitGate && (
                <SearchItem
                  name="documentNumber"
                  label="E-goods out (Số chứng từ ra cổng)"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  <Input placeholder="Nhập số chứng từ" />
                </SearchItem>
              )}

              {/* Buttons */}
              <div className="mt-4 flex justify-end gap-2">
                <CustomButton
                  type="default"
                  htmlType="button"
                  loading={loading}
                  onClick={handleClose}
                >
                  {t('btn.cancel')}
                </CustomButton>
                <CustomButton
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                >
                  {t('btn.save')}
                </CustomButton>
              </div>
            </>
          )}
        </SearchForm>
      </Modal>

      {/* Camera Modal */}
      <Modal
        width={600}
        open={cameraOpen}
        destroyOnClose
        title="Chụp ảnh"
        footer={
          <div className="flex justify-end gap-2">
            <CustomButton
              type="default"
              onClick={capture}
            >
              Chụp
            </CustomButton>
            <CustomButton
              type="primary"
              onClick={() => setCameraOpen(false)}
            >
              Đóng
            </CustomButton>
          </div>
        }
        onCancel={() => setCameraOpen(false)}
      >
        {cameraOpen && (
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            minScreenshotHeight={1080}
            minScreenshotWidth={1080}
          />
        )}
      </Modal>
    </>
  );
};

export default TransitionButtonModal;

